import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

interface SolutionLayoutProps {
  title: string;
  tagline: string;
  badge?: string;
  children: React.ReactNode;
  breadcrumbLabel: string;
}

export default function SolutionLayout({ title, tagline, badge, children, breadcrumbLabel }: SolutionLayoutProps) {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden relative">
          <div className="absolute inset-0 vx-grid-bg pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-primary-100/60 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <Link to="/solutions/wholesale-voice-termination" className="hover:text-primary-600 cursor-pointer">Solutions</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-foreground-800">{breadcrumbLabel}</span>
            </nav>
            {badge && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-5">
                {badge}
              </div>
            )}
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight tracking-tight max-w-4xl">
              {title}
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-700 max-w-3xl leading-relaxed">
              {tagline}
            </p>
          </div>
        </section>

        {children}

        {/* Bottom CTA */}
        <section className="py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-background-50 p-8 md:p-14">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-400/40 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-background-50/10 blur-3xl pointer-events-none" />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8">
                  <h2 className="text-2xl md:text-4xl font-semibold leading-tight font-heading">
                    Talk to our carrier team about {title.toLowerCase()}
                  </h2>
                  <p className="mt-4 text-background-100/90 text-sm md:text-base max-w-2xl">
                    Share your traffic profile and top destinations — we&apos;ll reply with a targeted rate deck and a suggested routing profile within one business day.
                  </p>
                </div>
                <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-background-50 text-primary-700 hover:bg-background-100 px-6 py-3.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Request a quote
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                  </Link>
                  <Link
                    to="/coverage"
                    className="inline-flex items-center justify-center gap-2 bg-primary-700/40 hover:bg-primary-700/60 text-background-50 border border-background-50/30 px-6 py-3.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-search-line w-4 h-4 flex items-center justify-center" />
                    Search destinations
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}